import jsPDF from 'jspdf';
import { format } from 'date-fns';
import autoTable from 'jspdf-autotable';

export interface InvoiceData {
  id: string;
  merchant: string;
  merchantAddress?: string;
  merchantEmail?: string;
  memberName?: string;
  memberId?: string;
  memberEmail?: string;
  issueDate: string;
  dueDate: string;
  paidDate?: string;
  status: string;
  paymentMethod?: string;
  subtotal: number;
  taxRate: number;
  tax: number;
  total: number;
  transactions: Array<{
    id: string;
    date: string;
    description: string;
    points: number;
    amount: number;
  }>;
}

export const generateInvoicePDF = (invoice: InvoiceData): jsPDF => {
  const doc = new jsPDF();
  
  // Set font
  doc.setFont('helvetica');
  
  // Enhanced gradient header with smoother transition
  const headerHeight = 50;
  for (let i = 0; i < headerHeight; i++) {
    const ratio = i / headerHeight;
    const purpleIntensity = Math.floor(147 * (1 - ratio * 0.3));
    const blueIntensity = Math.floor(51 + ratio * 80);
    doc.setFillColor(purpleIntensity, blueIntensity, 234);
    doc.rect(0, i, 210, 1, 'F');
  }
  
  // Enhanced header section with better typography
  doc.setFontSize(32);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text('INVOICE', 20, 28);
  
  // Invoice ID with modern styling
  doc.setFontSize(16);
  doc.setFont('helvetica', 'normal');
  doc.text(`#${invoice.id}`, 20, 40);
  
  // Modern status badge with better positioning and styling
  const statusY = 60;
  const statusColors = {
    paid: { bg: [34, 197, 94], text: [255, 255, 255] },
    pending: { bg: [234, 179, 8], text: [255, 255, 255] },
    overdue: { bg: [239, 68, 68], text: [255, 255, 255] }
  };
  const statusColor = statusColors[invoice.status as keyof typeof statusColors] || { bg: [107, 114, 128], text: [255, 255, 255] };
  
  // Enhanced status badge with shadow effect
  doc.setFillColor(0, 0, 0, 0.1);
  doc.roundedRect(21, statusY + 1, 35, 10, 3, 3, 'F');
  doc.setFillColor(statusColor.bg[0], statusColor.bg[1], statusColor.bg[2]);
  doc.roundedRect(20, statusY, 35, 10, 3, 3, 'F');
  doc.setTextColor(statusColor.text[0], statusColor.text[1], statusColor.text[2]);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(invoice.status.toUpperCase(), 22, statusY + 7);
  
  // Issue date with better styling
  doc.setTextColor(107, 114, 128);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`Issued: ${format(new Date(invoice.issueDate), 'MMM d, yyyy')}`, 65, statusY + 7);
  
  // Reset color
  doc.setTextColor(0, 0, 0);
  
  // Enhanced merchant and member cards with better spacing and shadows
  const cardY = 85;
  const cardWidth = 85;
  const cardHeight = 45;
  
  // Merchant card with enhanced styling
  // Shadow effect
  doc.setFillColor(0, 0, 0, 0.05);
  doc.roundedRect(21, cardY + 1, cardWidth, cardHeight, 4, 4, 'F');
  // Main card
  doc.setFillColor(249, 250, 251);
  doc.roundedRect(20, cardY, cardWidth, cardHeight, 4, 4, 'F');
  doc.setDrawColor(229, 231, 235);
  doc.setLineWidth(0.5);
  doc.roundedRect(20, cardY, cardWidth, cardHeight, 4, 4, 'S');
  
  // Merchant header with icon
  doc.setFontSize(13);
  doc.setTextColor(55, 65, 81);
  doc.setFont('helvetica', 'bold');
  doc.text('🏢 Merchant Details', 25, cardY + 12);
  
  // Merchant content with better typography
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(17, 24, 39);
  doc.text(invoice.merchant.substring(0, 25), 25, cardY + 22);
  
  if (invoice.merchantAddress) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(107, 114, 128);
    const addressLines = doc.splitTextToSize(invoice.merchantAddress, cardWidth - 10);
    doc.text(addressLines, 25, cardY + 30);
  }
  
  if (invoice.merchantEmail) {
    doc.setTextColor(59, 130, 246);
    doc.text(invoice.merchantEmail.substring(0, 20), 25, cardY + 38);
  }
  
  // Member card with enhanced styling
  const memberCardX = 110;
  // Shadow effect
  doc.setFillColor(0, 0, 0, 0.05);
  doc.roundedRect(memberCardX + 1, cardY + 1, cardWidth, cardHeight, 4, 4, 'F');
  // Main card
  doc.setFillColor(249, 250, 251);
  doc.roundedRect(memberCardX, cardY, cardWidth, cardHeight, 4, 4, 'F');
  doc.setDrawColor(229, 231, 235);
  doc.roundedRect(memberCardX, cardY, cardWidth, cardHeight, 4, 4, 'S');
  
  // Member header with icon
  doc.setFontSize(13);
  doc.setTextColor(55, 65, 81);
  doc.setFont('helvetica', 'bold');
  doc.text('👤 Member Details', memberCardX + 5, cardY + 12);
  
  // Member content with better typography
  if (invoice.memberId) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(17, 24, 39);
    doc.text(`Member #${invoice.memberId}`, memberCardX + 5, cardY + 22);
  }
  
  if (invoice.memberName) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(107, 114, 128);
    doc.text(invoice.memberName, memberCardX + 5, cardY + 30);
  }
  
  if (invoice.memberEmail) {
    doc.setTextColor(59, 130, 246);
    doc.setFontSize(9);
    doc.text(invoice.memberEmail.substring(0, 20), memberCardX + 5, cardY + 38);
  }
  
  // Enhanced transactions section with better spacing
  const transactionsY = cardY + 60;
  
  // Transactions header with modern styling
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(229, 231, 235);
  doc.roundedRect(20, transactionsY, 170, 12, 3, 3, 'FD');
  
  doc.setFontSize(14);
  doc.setTextColor(55, 65, 81);
  doc.setFont('helvetica', 'bold');
  doc.text('📄 Invoice Transactions', 25, transactionsY + 8);
  
  // Enhanced table headers with better styling
  const headerY = transactionsY + 20;
  doc.setFillColor(243, 244, 246);
  doc.rect(20, headerY - 4, 170, 10, 'F');
  doc.setDrawColor(229, 231, 235);
  doc.rect(20, headerY - 4, 170, 10, 'S');
  
  doc.setFontSize(10);
  doc.setTextColor(55, 65, 81);
  doc.setFont('helvetica', 'bold');
  doc.text('ID', 25, headerY + 2);
  doc.text('Date', 55, headerY + 2);
  doc.text('Description', 85, headerY + 2);
  doc.text('Points', 140, headerY + 2);
  doc.text('Amount', 165, headerY + 2);
  
  // Enhanced transaction rows with better formatting
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  let yPos = headerY + 12;
  
  invoice.transactions.forEach((transaction, index) => {
    if (index % 2 === 0) {
      doc.setFillColor(249, 250, 251);
      doc.rect(20, yPos - 4, 170, 10, 'F');
    }
    
    doc.setTextColor(17, 24, 39);
    doc.text(transaction.id, 25, yPos + 2);
    doc.setTextColor(107, 114, 128);
    doc.text(format(new Date(transaction.date), 'MMM d'), 55, yPos + 2);
    doc.setTextColor(17, 24, 39);
    doc.text(transaction.description.substring(0, 22), 85, yPos + 2);
    doc.setTextColor(147, 51, 234);
    doc.text(transaction.points.toString(), 140, yPos + 2);
    doc.setTextColor(17, 24, 39);
    doc.setFont('helvetica', 'bold');
    doc.text(`$${transaction.amount.toFixed(2)}`, 165, yPos + 2);
    doc.setFont('helvetica', 'normal');
    yPos += 10;
  });
  
  // Enhanced totals section with modern card design
  yPos += 15;
  const totalsCardHeight = 35;
  
  // Shadow effect for totals card
  doc.setFillColor(0, 0, 0, 0.05);
  doc.roundedRect(121, yPos + 1, 70, totalsCardHeight, 4, 4, 'F');
  // Main totals card
  doc.setFillColor(249, 250, 251);
  doc.roundedRect(120, yPos, 70, totalsCardHeight, 4, 4, 'F');
  doc.setDrawColor(229, 231, 235);
  doc.roundedRect(120, yPos, 70, totalsCardHeight, 4, 4, 'S');
  
  // Totals content with better spacing
  doc.setFontSize(11);
  doc.setTextColor(107, 114, 128);
  doc.setFont('helvetica', 'normal');
  doc.text('Subtotal:', 125, yPos + 10);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.text(`$${invoice.subtotal.toFixed(2)}`, 165, yPos + 10);
  
  doc.setTextColor(107, 114, 128);
  doc.setFont('helvetica', 'normal');
  doc.text(`Tax (${invoice.taxRate}%):`, 125, yPos + 18);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.text(`$${invoice.tax.toFixed(2)}`, 165, yPos + 18);
  
  // Enhanced total with emphasis and separator
  doc.setDrawColor(229, 231, 235);
  doc.line(125, yPos + 22, 185, yPos + 22);
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(17, 24, 39);
  doc.text('Total:', 125, yPos + 30);
  doc.setTextColor(147, 51, 234);
  doc.text(`$${invoice.total.toFixed(2)}`, 165, yPos + 30);
  
  // Enhanced payment details section with modern styling
  if (invoice.paymentMethod) {
    yPos += 50;
    // Shadow effect
    doc.setFillColor(0, 0, 0, 0.05);
    doc.roundedRect(21, yPos + 1, 170, 20, 4, 4, 'F');
    // Main payment card
    doc.setFillColor(239, 246, 255);
    doc.roundedRect(20, yPos, 170, 20, 4, 4, 'F');
    doc.setDrawColor(147, 197, 253);
    doc.roundedRect(20, yPos, 170, 20, 4, 4, 'S');
    
    // Payment header with icon
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(37, 99, 235);
    doc.text('💳 Payment Details', 25, yPos + 8);
    
    // Payment details with better formatting
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(55, 65, 81);
    const paymentText = `${invoice.paymentMethod} • ${invoice.status === "paid" ? `Paid on ${format(new Date(invoice.paidDate || invoice.dueDate), "MMM d, yyyy")}` : `Due on ${format(new Date(invoice.dueDate), "MMM d, yyyy")}`}`;
    doc.text(paymentText, 25, yPos + 15);
  }
  
  // Enhanced footer with branding
  const footerY = 280;
  doc.setFillColor(249, 250, 251);
  doc.rect(0, footerY, 210, 17, 'F');
  doc.setDrawColor(229, 231, 235);
  doc.line(0, footerY, 210, footerY);
  
  doc.setFontSize(8);
  doc.setTextColor(107, 114, 128);
  doc.text(`Generated on ${format(new Date(), "MMM d, yyyy 'at' h:mm a")}`, 20, footerY + 8);
  doc.text('Invoice Management System', 20, footerY + 14);
  
  return doc;
};

export const downloadInvoicePDF = (invoice: InvoiceData) => {
  const pdf = generateInvoicePDF(invoice);
  pdf.save(`invoice-${invoice.id}.pdf`);
};


export const generateAllInvoicesPDF = (invoices: InvoiceData[]): jsPDF => {
  const doc = new jsPDF();
  
  // Remove the first empty page that gets created by default
  doc.deletePage(1);
  
  invoices.forEach((invoice, index) => {
    // Add a new page for each invoice
    doc.addPage();
    
    // Generate the invoice content directly on the current page
    generateInvoiceContent(doc, invoice);
  });
  
  return doc;
};

// Helper function to generate invoice content on a specific page
const generateInvoiceContent = (doc: jsPDF, invoice: InvoiceData) => {
  // Use the same enhanced styling as the main generateInvoicePDF function
  // Set font
  doc.setFont('helvetica');
  
  // Enhanced gradient header with smoother transition
  const headerHeight = 50;
  for (let i = 0; i < headerHeight; i++) {
    const ratio = i / headerHeight;
    const purpleIntensity = Math.floor(147 * (1 - ratio * 0.3));
    const blueIntensity = Math.floor(51 + ratio * 80);
    doc.setFillColor(purpleIntensity, blueIntensity, 234);
    doc.rect(0, i, 210, 1, 'F');
  }
  
  // Enhanced header section with better typography
  doc.setFontSize(32);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text('INVOICE', 20, 28);
  
  // Invoice ID with modern styling
  doc.setFontSize(16);
  doc.setFont('helvetica', 'normal');
  doc.text(`#${invoice.id}`, 20, 40);
  
  // Modern status badge with better positioning and styling
  const statusY = 60;
  const statusColors = {
    paid: { bg: [34, 197, 94], text: [255, 255, 255] },
    pending: { bg: [234, 179, 8], text: [255, 255, 255] },
    overdue: { bg: [239, 68, 68], text: [255, 255, 255] }
  };
  const statusColor = statusColors[invoice.status as keyof typeof statusColors] || { bg: [107, 114, 128], text: [255, 255, 255] };
  
  // Enhanced status badge with shadow effect
  doc.setFillColor(0, 0, 0, 0.1);
  doc.roundedRect(21, statusY + 1, 35, 10, 3, 3, 'F');
  doc.setFillColor(statusColor.bg[0], statusColor.bg[1], statusColor.bg[2]);
  doc.roundedRect(20, statusY, 35, 10, 3, 3, 'F');
  doc.setTextColor(statusColor.text[0], statusColor.text[1], statusColor.text[2]);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(invoice.status.toUpperCase(), 22, statusY + 7);
  
  // Issue date with better styling
  doc.setTextColor(107, 114, 128);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`Issued: ${format(new Date(invoice.issueDate), 'MMM d, yyyy')}`, 65, statusY + 7);
  
  // Reset color
  doc.setTextColor(0, 0, 0);
  
  // Enhanced merchant and member cards with better spacing and shadows
  const cardY = 85;
  const cardWidth = 85;
  const cardHeight = 45;
  
  // Merchant card with enhanced styling
  // Shadow effect
  doc.setFillColor(0, 0, 0, 0.05);
  doc.roundedRect(21, cardY + 1, cardWidth, cardHeight, 4, 4, 'F');
  // Main card
  doc.setFillColor(249, 250, 251);
  doc.roundedRect(20, cardY, cardWidth, cardHeight, 4, 4, 'F');
  doc.setDrawColor(229, 231, 235);
  doc.setLineWidth(0.5);
  doc.roundedRect(20, cardY, cardWidth, cardHeight, 4, 4, 'S');
  
  // Merchant header with icon
  doc.setFontSize(13);
  doc.setTextColor(55, 65, 81);
  doc.setFont('helvetica', 'bold');
  doc.text('🏢 Merchant Details', 25, cardY + 12);
  
  // Merchant content with better typography
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(17, 24, 39);
  doc.text(invoice.merchant.substring(0, 25), 25, cardY + 22);
  
  if (invoice.merchantAddress) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(107, 114, 128);
    const addressLines = doc.splitTextToSize(invoice.merchantAddress, cardWidth - 10);
    doc.text(addressLines, 25, cardY + 30);
  }
  
  if (invoice.merchantEmail) {
    doc.setTextColor(59, 130, 246);
    doc.text(invoice.merchantEmail.substring(0, 20), 25, cardY + 38);
  }
  
  // Member card with enhanced styling
  const memberCardX = 110;
  // Shadow effect
  doc.setFillColor(0, 0, 0, 0.05);
  doc.roundedRect(memberCardX + 1, cardY + 1, cardWidth, cardHeight, 4, 4, 'F');
  // Main card
  doc.setFillColor(249, 250, 251);
  doc.roundedRect(memberCardX, cardY, cardWidth, cardHeight, 4, 4, 'F');
  doc.setDrawColor(229, 231, 235);
  doc.roundedRect(memberCardX, cardY, cardWidth, cardHeight, 4, 4, 'S');
  
  // Member header with icon
  doc.setFontSize(13);
  doc.setTextColor(55, 65, 81);
  doc.setFont('helvetica', 'bold');
  doc.text('👤 Member Details', memberCardX + 5, cardY + 12);
  
  // Member content with better typography
  if (invoice.memberId) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(17, 24, 39);
    doc.text(`Member #${invoice.memberId}`, memberCardX + 5, cardY + 22);
  }
  
  if (invoice.memberName) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(107, 114, 128);
    doc.text(invoice.memberName, memberCardX + 5, cardY + 30);
  }
  
  if (invoice.memberEmail) {
    doc.setTextColor(59, 130, 246);
    doc.setFontSize(9);
    doc.text(invoice.memberEmail.substring(0, 20), memberCardX + 5, cardY + 38);
  }
  
  // Enhanced transactions section with better spacing
  const transactionsY = cardY + 60;
  
  // Transactions header with modern styling
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(229, 231, 235);
  doc.roundedRect(20, transactionsY, 170, 12, 3, 3, 'FD');
  
  doc.setFontSize(14);
  doc.setTextColor(55, 65, 81);
  doc.setFont('helvetica', 'bold');
  doc.text('📄 Invoice Transactions', 25, transactionsY + 8);
  
  // Enhanced table headers with better styling
  const headerY = transactionsY + 20;
  doc.setFillColor(243, 244, 246);
  doc.rect(20, headerY - 4, 170, 10, 'F');
  doc.setDrawColor(229, 231, 235);
  doc.rect(20, headerY - 4, 170, 10, 'S');
  
  doc.setFontSize(10);
  doc.setTextColor(55, 65, 81);
  doc.setFont('helvetica', 'bold');
  doc.text('ID', 25, headerY + 2);
  doc.text('Date', 55, headerY + 2);
  doc.text('Description', 85, headerY + 2);
  doc.text('Points', 140, headerY + 2);
  doc.text('Amount', 165, headerY + 2);
  
  // Enhanced transaction rows with better formatting
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  let yPos = headerY + 12;
  
  invoice.transactions.forEach((transaction, index) => {
    if (index % 2 === 0) {
      doc.setFillColor(249, 250, 251);
      doc.rect(20, yPos - 4, 170, 10, 'F');
    }
    
    doc.setTextColor(17, 24, 39);
    doc.text(transaction.id, 25, yPos + 2);
    doc.setTextColor(107, 114, 128);
    doc.text(format(new Date(transaction.date), 'MMM d'), 55, yPos + 2);
    doc.setTextColor(17, 24, 39);
    doc.text(transaction.description.substring(0, 22), 85, yPos + 2);
    doc.setTextColor(147, 51, 234);
    doc.text(transaction.points.toString(), 140, yPos + 2);
    doc.setTextColor(17, 24, 39);
    doc.setFont('helvetica', 'bold');
    doc.text(`$${transaction.amount.toFixed(2)}`, 165, yPos + 2);
    doc.setFont('helvetica', 'normal');
    yPos += 10;
  });
  
  // Enhanced totals section with modern card design
  yPos += 15;
  const totalsCardHeight = 35;
  
  // Shadow effect for totals card
  doc.setFillColor(0, 0, 0, 0.05);
  doc.roundedRect(121, yPos + 1, 70, totalsCardHeight, 4, 4, 'F');
  // Main totals card
  doc.setFillColor(249, 250, 251);
  doc.roundedRect(120, yPos, 70, totalsCardHeight, 4, 4, 'F');
  doc.setDrawColor(229, 231, 235);
  doc.roundedRect(120, yPos, 70, totalsCardHeight, 4, 4, 'S');
  
  // Totals content with better spacing
  doc.setFontSize(11);
  doc.setTextColor(107, 114, 128);
  doc.setFont('helvetica', 'normal');
  doc.text('Subtotal:', 125, yPos + 10);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.text(`$${invoice.subtotal.toFixed(2)}`, 165, yPos + 10);
  
  doc.setTextColor(107, 114, 128);
  doc.setFont('helvetica', 'normal');
  doc.text(`Tax (${invoice.taxRate}%):`, 125, yPos + 18);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.text(`$${invoice.tax.toFixed(2)}`, 165, yPos + 18);
  
  // Enhanced total with emphasis and separator
  doc.setDrawColor(229, 231, 235);
  doc.line(125, yPos + 22, 185, yPos + 22);
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(17, 24, 39);
  doc.text('Total:', 125, yPos + 30);
  doc.setTextColor(147, 51, 234);
  doc.text(`$${invoice.total.toFixed(2)}`, 165, yPos + 30);
  
  // Enhanced payment details section with modern styling
  if (invoice.paymentMethod) {
    yPos += 50;
    // Shadow effect
    doc.setFillColor(0, 0, 0, 0.05);
    doc.roundedRect(21, yPos + 1, 170, 20, 4, 4, 'F');
    // Main payment card
    doc.setFillColor(239, 246, 255);
    doc.roundedRect(20, yPos, 170, 20, 4, 4, 'F');
    doc.setDrawColor(147, 197, 253);
    doc.roundedRect(20, yPos, 170, 20, 4, 4, 'S');
    
    // Payment header with icon
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(37, 99, 235);
    doc.text('💳 Payment Details', 25, yPos + 8);
    
    // Payment details with better formatting
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(55, 65, 81);
    const paymentText = `${invoice.paymentMethod} • ${invoice.status === "paid" ? `Paid on ${format(new Date(invoice.paidDate || invoice.dueDate), "MMM d, yyyy")}` : `Due on ${format(new Date(invoice.dueDate), "MMM d, yyyy")}`}`;
    doc.text(paymentText, 25, yPos + 15);
  }
  
  // Enhanced footer with branding
  const footerY = 280;
  doc.setFillColor(249, 250, 251);
  doc.rect(0, footerY, 210, 17, 'F');
  doc.setDrawColor(229, 231, 235);
  doc.line(0, footerY, 210, footerY);
  
  doc.setFontSize(8);
  doc.setTextColor(107, 114, 128);
  doc.text(`Generated on ${format(new Date(), "MMM d, yyyy 'at' h:mm a")}`, 20, footerY + 8);
  doc.text('Invoice Management System', 20, footerY + 14);
};

export const downloadAllInvoicesPDF = (invoices: InvoiceData[]) => {
  const pdf = generateAllInvoicesPDF(invoices);
  pdf.save(`all-invoices-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
};

// Transaction export utilities
export interface TransactionExportData {
  id: string;
  program: string;
  merchant: string;
  memberId: string;
  points: string | number;
  amount: string | number;
  currency: string;
  status: string;
  date: string;
  time: string;
  cashback: string | number;
}

export const generateTransactionsCSV = (transactions: any[]): string => {
  const headers = [
    'Date & Time',
    'Member Id (From)',
    'Member Id (To)',
    'Transaction Id',
    'Partner Name',
    'Points Redeemed',
    'Redemption Amount',
    'Points Earned',
    'Accrued Amount',
    'Program',
    'Status',
  ];
  const csvContent = [
    headers.join(','),
    ...transactions.map(tx => headers.map(h => `"${String(tx[h] ?? '').replace(/"/g, '""')}"`).join(','))
  ].join('\n');
  return csvContent;
};

export const downloadTransactionsCSV = (transactions: any[]) => {
  const csvContent = generateTransactionsCSV(transactions);
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `transactions-${format(new Date(), 'yyyy-MM-dd')}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadTransactionsPDF = (transactions: any[]) => {
  const doc = new jsPDF();
  const headers = [
    'Date & Time',
    'Member Id (From)',
    'Member Id (To)',
    'Transaction Id',
    'Partner Name',
    'Points Redeemed',
    'Redemption Amount',
    'Points Earned',
    'Accrued Amount',
    'Program',
    'Status',
  ];
  const tableRows = transactions.map(tx => headers.map(h => tx[h] ?? ''));
  autoTable(doc, {
    head: [headers],
    body: tableRows,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [41, 128, 185] },
    margin: { top: 20 },
  });
  doc.save(`transactions-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
};

// Add this function for Orders export
export const generateOrdersCSV = (orders: any[]): string => {
  const headers = [
    'Order ID',
    'Created Date',
    'Member ID',
    'Program',
    'Merchant',
    'Order Amount',
    'Currency',
    'Order Status',
  ];
  const csvContent = [
    headers.join(','),
    ...orders.map(order => headers.map(h => `"${String(order[h] ?? '').replace(/"/g, '""')}"`).join(','))
  ].join('\n');
  return csvContent;
};

// Update downloadTransactionsCSV to accept custom headers and generator
export const downloadCSV = (data: any[], generator: (data: any[]) => string, filename: string) => {
  const csvContent = generator(data);
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}-${format(new Date(), 'yyyy-MM-dd')}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadOrdersPDF = (orders: any[]) => {
  const doc = new jsPDF();
  const headers = [
    'Order ID',
    'Created Date',
    'Member ID',
    'Program',
    'Merchant',
    'Order Amount',
    'Currency',
    'Order Status',
  ];
  const tableRows = orders.map(order => headers.map(h => order[h] ?? ''));
  autoTable(doc, {
    head: [headers],
    body: tableRows,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [41, 128, 185] },
    margin: { top: 20 },
  });
  doc.save(`orders-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
};
